// C.1 — пакет реализаций для юнитов Лив Лайфа из v6.
// Реализованы: heal-юниты (Медтех LL-1, Лекарь LL-4, Хирург LL-7,
// Боевой медик, Хирург Vita, Био-агент «Мор», Регенератор «Гидра»).
//
// Сложные карты (Капсула регенерации, Исхак, Глав-медик Лазарь и т.п.) —
// отдельные файлы или следующая сессия.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { healUnit } from '../heal.js';
import { addInfectedStack } from '../../../keywords/infected.js';
import { resolveTarget } from '../../../effects/targets.js';
import { registerOnEndOfTurnExtraHandler, registerOnTurnStartExtraHandler } from '../../deus/hooks.js';
import { registerC1Order } from '../../global/c1/order-registry.js';
import { applyPermanentBuff } from '../../../buffs.js';
import { getHealTarget } from '../../../heal-priority.js';

// === Медтех LL-1 (livlife_medtekh_ll1) ===
// trigger turn_end: heal 1 союзнику с lowest hp.
const MEDTEKH_ID = 'livlife_medtekh_ll1';
function medtekhEot(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return;
  for (const row of p.field) {
    for (const u of row || []) {
      if (!u || u.id !== MEDTEKH_ID) continue;
      if (u.current?.silenced) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      const t = getHealTarget(state, u);
      if (t) healUnit(state, t, 1, u);
    }
  }
}
registerImplementedAbility(MEDTEKH_ID, 'trigger');

// === Лекарь LL-4 (livlife_lekar_ll4) ===
// trigger turn_end: heal 3 союзнику lowest_hp (по приоритету лечения).
// Редизайн фаундера: хил 1→3, доп.триггер «возврат карты в руку» убран
// (см. units-batch2.js).
const LEKAR_ID = 'livlife_lekar_ll4';
function lekarEot(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return;
  for (const row of p.field) {
    for (const u of row || []) {
      if (!u || u.id !== LEKAR_ID) continue;
      if (u.current?.silenced) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      const t = getHealTarget(state, u);
      if (t) healUnit(state, t, 3, u);
    }
  }
}
registerImplementedAbility(LEKAR_ID, 'trigger');

// === Хирург LL-7 (livlife_hirurg_ll7) ===
// trigger turn_end: heal 2 союзнику lowest_hp.
const HIRURG_LL7_ID = 'livlife_hirurg_ll7';
function hirurgLl7Eot(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return;
  for (const row of p.field) {
    for (const u of row || []) {
      if (!u || u.id !== HIRURG_LL7_ID) continue;
      if (u.current?.silenced) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      const t = getHealTarget(state, u);
      if (t) healUnit(state, t, 2, u);
    }
  }
}
registerImplementedAbility(HIRURG_LL7_ID, 'trigger');

// === Боевой медик (livlife_boevoy_medik) ===
// trigger turn_start: heal 1 союзнику lowest_hp.
const BMEDIK_ID = 'livlife_boevoy_medik';
function bmedikSot(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return;
  for (const row of p.field) {
    for (const u of row || []) {
      if (!u || u.id !== BMEDIK_ID) continue;
      if (u.current?.silenced) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      const targets = resolveTarget(state, u, 'ally_lowest_hp');
      if (targets.length > 0) healUnit(state, targets[0], 1, u);
    }
  }
}
registerImplementedAbility(BMEDIK_ID, 'trigger');

// === Хирург Vita (livlife_hirurg_vita) ===
// Order: лечит союзного юнита ПО ВЫБОРУ на 2 hp (2d: picker в UI пишет opts.target).
// Без выбора (бот/sandbox/нет цели) — дефолт ally_lowest_hp.
const HIRURG_VITA_ID = 'livlife_hirurg_vita';
export function hirurgVitaOrder(state, sourceCard, opts = {}) {
  let target = opts.target;
  if (!target || !target.isAlive || !target.isAlive()) {
    const targets = resolveTarget(state, sourceCard, 'ally_lowest_hp');
    if (targets.length === 0) return { ok: false, reason: 'no_target' };
    target = targets[0];
  }
  healUnit(state, target, 2, sourceCard);
  return { ok: true };
}
registerImplementedAbility(HIRURG_VITA_ID, 'order');
registerC1Order(HIRURG_VITA_ID, hirurgVitaOrder);

// === Био-агент «Мор» (livlife_bio_agent_mor) ===
// Order: 1 стак Заражения вражескому юниту (picker).
const BIO_MOR_ID = 'livlife_bio_agent_mor';
export function bioMorOrder(state, sourceCard, opts = {}) {
  const target = opts.target;
  if (!target) return { ok: false, reason: 'no_target' };
  if (target.ownerId === sourceCard.ownerId) return { ok: false, reason: 'not_enemy' };
  addInfectedStack(state, target, BIO_MOR_ID, 1);
  return { ok: true };
}
registerImplementedAbility(BIO_MOR_ID, 'order');
registerC1Order(BIO_MOR_ID, bioMorOrder);

// === Регенератор «Гидра» (livlife_regenerator_gidra) ===
// Trigger turn_end: heal 2 самому раненому союзнику + сама Гидра +0/1/0 (броня).
const GIDRA_ID = 'livlife_regenerator_gidra';
function gidraEot(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return;
  for (const row of p.field) {
    for (const u of row || []) {
      if (!u || u.id !== GIDRA_ID) continue;
      if (u.current?.silenced) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      const targets = resolveTarget(state, u, 'ally_lowest_hp');
      if (targets.length > 0) healUnit(state, targets[0], 2, u);
      // Гидра сама получает +0/1/0 (броня) каждый ход — регенерация панциря.
      applyPermanentBuff(state, u, { atk: 0, armor: 1, hp: 0 }, GIDRA_ID, 'Гидра');
    }
  }
}
registerImplementedAbility(GIDRA_ID, 'trigger');

// === Аугмент «Стрелок» (livlife_augment_strelok) ===
// vanilla — no DSL. Регистрируем как «нет способностей».
// (Не нужен файл, но регистрация полезна чтобы не было warn.)

// === Регистрация всех EOT/SOT триггеров ===
registerOnEndOfTurnExtraHandler((state) => {
  for (let i = 0; i < state.players.length; i++) {
    medtekhEot(state, i);
    lekarEot(state, i);
    hirurgLl7Eot(state, i);
    gidraEot(state, i);
  }
});
registerOnTurnStartExtraHandler((state) => {
  for (let i = 0; i < state.players.length; i++) {
    bmedikSot(state, i);
  }
});
